/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import {
  useContext, useEffect, useImperativeHandle, memo, forwardRef, useState, useCallback,
} from 'react';
import type { ReactNode } from 'react';
import isEmpty from 'lodash/isEmpty';
import usePrevious from 'react-use/lib/usePrevious';
import isDeepEqual from 'fast-deep-equal/react';
import { Formik } from 'formik';
import type { FormikConfig, FormikProps } from 'formik';
import { usePrompt, PromtContext } from './Prompt';

import { FormContext } from './utils';
import { useComponentDidMount, useComponentWillUnmount } from '../../hooks';

const defaultShouldTriggerErrors = (errors: any, nextErrors: any): boolean => !isDeepEqual(errors, nextErrors);

// formik 2.4.x не подставляет пустой объект для отсутствующих initialValues:
// values остаётся undefined, и первый же сабмит падает в редьюсере Formik
// (SUBMIT_ATTEMPT → setNestedObjectValues(undefined)). Form по контракту
// допускает форму без initialValues (FormModal-«транспорт» без полей —
// например, модалка клиента мастера ШМ), поэтому подставляем стабильный
// пустой объект. Константа уровня модуля, чтобы enableReinitialize не
// сравнивал новые {} на каждом рендере.
const EMPTY_INITIAL_VALUES: Record<string, never> = {};

type WithOnErrorHandlerProps = {
  onError: (errors: any) => void;
  shouldTriggerErrors?: (errors: any, nextErrors: any) => boolean;
} & FormikProps<any>;

const WithOnErrorHandler = memo(({ onError, shouldTriggerErrors, ...formik }: WithOnErrorHandlerProps) => {
  const shouldTriggerErrorsFn = shouldTriggerErrors || defaultShouldTriggerErrors;
  const [errors, updateErrors] = useState(formik.errors);

  useEffect(() => {
    if (!isEmpty(formik.errors) && shouldTriggerErrorsFn(errors, formik.errors)) {
      onError(formik.errors);

      updateErrors(formik.errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.errors]);

  return null;
});

type WithOnChangeHandlerProps = {
  onChange: (prevValues: any, values: any, formikBag: any) => void;
} & FormikProps<any>;

const WithOnChangeHandler = memo(({ onChange, ...formik }: WithOnChangeHandlerProps) => {
  const { values } = formik;
  const prevValues = usePrevious(values);

  useEffect(() => {
    // Don't run effect on form init
    if (prevValues) {
      onChange(prevValues, values, formik);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return null;
});

/* const PromptIfDirty = (props) => {
  const {
    message,
    onConfirmPrompt,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onCancelPrompt,
  } = props;
  const { dirty } = useContext(FormContext);

  // const isDirty = !!value;

  // usePrompt(dirty);

  return (<div>rrr</div>);

   return (
    <NavigationPrompt when={ dirty }>
      { (isOpen, onConfirm, onCancel) => (
        <Modal
          show={ isOpen }
          backdrop='static'
          size='lg'
          aria-labelledby='prompt-modal'
          centered
        >
          <Modal.Header>
            <Modal.Title>
              { message }
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className='float-end'>
              <Button
                className='button-margin text-margin'
                variant='primary'
                icon='window-close'
                text='НЕТ. Отмена'
                onClick={ async () => {
                  if (typeof onCancelPrompt === 'function') {
                    await onCancelPrompt();
                  }
                  onCancel();
                } }
              />
              <Button
                className='button-margin text-margin btn-danger'
                variant='primary'
                icon='trash'
                text='ДА. Потерять изменения'
                onClick={ async () => {
                  if (typeof onConfirmPrompt === 'function') {
                    await onConfirmPrompt();
                  }
                  onConfirm();
                } }
              />
            </Row>
          </Modal.Body>
        </Modal>
      ) }
    </NavigationPrompt>
  );
}; */

/** Пропсы внутреннего FormComponent: конфиг Formik + его рантайм-состояние + свои пропсы Form. */
type FormComponentProps = FormikConfig<any> & FormikProps<any> & {
  disabled?: boolean;
  readOnly?: boolean;
  promptIfDirty?: boolean;
  promptIfDirtyMessage?: string;
  onError?: (errors: any) => void;
  onChange?: (prevValues: any, values: any, formikBag: any) => void;
  onReset?: (values: any, formikBag: any) => void;
  onConfirmPrompt?: (...args: unknown[]) => unknown;
  onCancelPrompt?: (...args: unknown[]) => unknown;
  children?: ReactNode | ((formProps: any) => ReactNode);
  [key: string]: any;
};

/* eslint-disable react/prop-types */
const FormComponent = forwardRef<any, FormComponentProps>((props, ref) => {
  const {
    promptIfDirty,
    promptIfDirtyMessage,
    children,
    onError,
    onChange,
    onReset,
    onConfirmPrompt,
    onCancelPrompt,
    ...formProps
  } = props;

  usePrompt((promptIfDirty && (formProps?.dirty ?? false)) ?? false);

  // пришлось переопределить событие onReset, так как оно стало неявно вызываться,
  // если выставлено свойство enableReinitialize для Formik'а
  // а у нас на onReset часто вешается функционал типа закрытия модального окна с формой
  // @see https://github.com/jaredpalmer/formik/pull/2201
  // @see https://github.com/jaredpalmer/formik/blob/v2.1.4/packages/formik/src/Formik.tsx#L445-L454
  const handleFormReset = ({ values, ...formikBag }) => {
    if (typeof onReset === 'function') {
      onReset(values, formikBag);
    }
  };

  formProps.resetForm = (nextInitialState) => {
    props.resetForm(nextInitialState);

    handleFormReset(props);
  };

  formProps.handleReset = () => {
    props.handleReset();

    handleFormReset(props);
  };

  useImperativeHandle(ref, () => ({
    ...formProps,
  }));

  return (
    // TODO - убрать react/jsx-no-constructed-context-values
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <FormContext.Provider value={ { ...formProps } }>
      { (typeof children === 'function') ? children(formProps) : children }

      { (typeof onError === 'function') && (
        <WithOnErrorHandler onError={ onError } { ...formProps } />
      ) }

      { (typeof onChange === 'function') && (
        <WithOnChangeHandler onChange={ onChange } { ...formProps } />
      ) }

      { /* promptIfDirty && (
        <PromptIfDirty
          message={ promptIfDirtyMessage }
          onConfirmPrompt={ onConfirmPrompt }
          onCancelPrompt={ onCancelPrompt }
        />
      ) */ }
    </FormContext.Provider>
  );
});

FormComponent.displayName = 'FormComponent';
/* eslint-enable react/prop-types */

/**
 * Компонент формы на основе Formik с расширенной функциональностью
 *
 * Поддерживаются все пропсы Formik
 * @see https://jaredpalmer.com/formik/docs/api/formik
 */
export interface FormProps extends Omit<FormikConfig<any>, 'onSubmit' | 'children'> {
  /** Не обязателен: Form используется и как «транспорт» значений без сабмита. */
  onSubmit?: (values: any, formikHelpers: any) => void | Promise<any>;
  disabled?: boolean;
  readOnly?: boolean;
  /** Показывать prompt при уходе с несохранённой формой. */
  promptIfDirty?: boolean;
  promptIfDirtyMessage?: string;
  /** render-prop: получает полный formik-пропс формы (то, что уходит в FormContext). */
  children?: ReactNode | ((formProps: any) => ReactNode);
  onError?: (errors: any) => void;
  onChange?: (prevValues: any, values: any) => void;
  onReset?: (values: any, formikBag: any) => void;
  onConfirmPrompt?: (...args: unknown[]) => unknown;
  onCancelPrompt?: (...args: unknown[]) => unknown;
  /**
   * Индексная сигнатура — часть существующего контракта: произвольные пропсы
   * (напр., loading, disableSubmitButton, error) НЕ деструктурируются и протекают
   * через {...formikConfig} в formProps → FormContext → render-prop детей.
   */
  [key: string]: any;
}

const Form = forwardRef<any, FormProps>((props, ref) => {
  const {
    validationSchema,
    initialValues,
    disabled,
    readOnly,
    promptIfDirty,
    promptIfDirtyMessage = 'Вы внесли изменения и не сохранили их. Вы действительно хотите покинуть страницу?',
    children,
    onError,
    onChange,
    onReset,
    onConfirmPrompt,
    onCancelPrompt,
    ...formikConfig
  } = props;

  // for debug purposes
  const handleErrors = useCallback((errors) => {
    if (process.env.DEBUG === 'true') {
      console.groupCollapsed(`%cFORM HAS ERRORS ${ (children as any)?.props?.header }`, 'color:#fd7e14');
      console.log(JSON.parse(JSON.stringify(errors)));
      console.groupEnd();
    }

    if (typeof onError === 'function') {
      onError(errors);
    }
  }, [onError, (children as any)?.props?.header]);

  const handleChange = useCallback((prevValues, values) => {
    if (typeof onChange === 'function') {
      onChange(prevValues, values);
    }
  }, [onChange]);

  // for debug purposes
  const handleReset = useCallback((values, formikBag) => {
    if (process.env.DEBUG === 'true') {
      console.groupCollapsed(`%cFORM WAS RESET ${ (children as any)?.props?.header }`, 'color:#6f42c1');
      if (values) console.log(JSON.parse(JSON.stringify(values)));
      console.groupEnd();
    }

    if (typeof onReset === 'function') {
      onReset(values, formikBag);
    }
  }, [onReset, (children as any)?.props?.header]);

  useComponentDidMount(() => {
    if (process.env.DEBUG === 'true') {
      console.log(`%cFORM WAS MOUNTED ${ (children as any)?.props?.header }`, 'color:#adb5bd');
    }
  });

  useComponentWillUnmount(() => {
    if (process.env.DEBUG === 'true') {
      console.log(`%cFORM WAS UNMOUNTED ${ (children as any)?.props?.header }`, 'color:#6c757d');
    }
  });

  return (
    <PromtContext>
      <Formik
        { ...(formikConfig as FormikConfig<any>) }
        initialValues={ initialValues ?? EMPTY_INITIAL_VALUES }
        validationSchema={ validationSchema }
      >
        { (formikProps) => (
          <FormComponent
            { ...formikConfig }
            { ...formikProps }
            initialValues={ initialValues }
            ref={ ref }
            validationSchema={ validationSchema }
            readOnly={ readOnly }
            disabled={ disabled }
            promptIfDirtyMessage={ promptIfDirtyMessage }
            promptIfDirty={ promptIfDirty }
            onError={ handleErrors }
            onChange={ handleChange }
            onReset={ handleReset }
            onConfirmPrompt={ onConfirmPrompt }
            onCancelPrompt={ onCancelPrompt }
          >
            { (formProps) => ((typeof children === 'function') ? children(formProps) : children) }
          </FormComponent>
        ) }
      </Formik>
    </PromtContext>
  );
});

Form.displayName = 'Form';

export default Form;
